import {ChatInputValidationError} from '@github-ui/copilot-chat/utils/validation-error'
import {ImageAttachButton} from '@github-ui/copilot-chat/components/ImageAttachButton'
import {useEntitlement} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {CopilotPlan} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {PaperAirplaneIcon} from '@primer/octicons-react'
import {FormControl, IconButton, Link, Stack} from '@primer/react'
import {useEffect, useRef, useState, useCallback} from 'react'
import {useAgentInfo} from '../../hooks/use-agent-info'
import {useTopLevelAgents} from '../../hooks/useTopLevelAgents'
import {useCreateAPISession, type CommandCreatedResponse} from '../../hooks/use-create-api-session'
import {
  type Session as SessionType,
  isPendingSession,
  isActiveSession,
  type Command,
  type CommandsResponse,
} from '../../types/session'
import styles from './TaskChatInputV2.module.css'
import type {AgentTaskAppPayload} from '../../types/agent-task-app-payload'
import {useCurrentUser} from '../../contexts/CurrentUserContext'
import {useCreateCommand} from '../../hooks/useCreateCommand'
import {useQueryClient} from '@github-ui/react-query'
import {TaskChatInputHeader} from './TaskChatInputHeader'
import {clsx} from 'clsx'
import {useIntentDetection} from '../../hooks/useIntentDetection'
import {baseRefMatchesHeadRef, getBranchArtifactData} from '../../utils/task-artifact-helpers'
import type {APITask} from '../../types/api-task'
import {useMarkdownImageHandling} from '../../hooks/use-markdown-image-handling'
import {ModeSelector} from '../new-task-chat-input/ModeSelector'
import {useIntentDetectionContext} from '../../contexts/IntentDetectionContext'
import {BlockedTaskInput} from './BlockedTaskInput'
import {ChatInputDisplay} from './ChatInputDisplay'
import {useChatStreamContext} from '../../contexts/ChatStreamContext'
import {useViewMode} from '../../contexts/ViewModeContext'
import {useUnarchiveTask} from '../../hooks/use-unarchive-task'
import {ModelPicker} from './ModelPicker'
import {useLatestAgentModel} from '../../hooks/use-latest-agent-model'
import {isCopilotCodingAgent, isCopilotOrCustomAgent} from '../../utils/agent-helpers'
import {TaskStopButton} from './TaskStopButton'
import {QuotaBanner} from './QuotaBanner'
import {PromptInput} from './PromptInput'
import {usePromptResponseSubmission} from '../../hooks/use-prompt-response-submission'
import {MANAGE_BUDGET_URL, UPGRADE_PLAN_URL} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {useChronicleAutocomplete} from '../../hooks/use-chronicle-autocomplete'
import {ChronicleAutocomplete} from '../chronicle-autocomplete/ChronicleAutocomplete'

interface TaskChatInputProps {
  taskAgentId?: number
  activeSession: SessionType | null
  onAddPendingSession: (text: string, options?: {agentId?: number; isPRComment?: boolean}) => void
  onClearPendingSession: () => void
  payload: AgentTaskAppPayload
  scrollToBottom: (options?: {offset?: number}) => void
  repoNwo: string
  task: APITask
  branchAliveChannel?: string
  onViewDiff?: (filePath?: string) => void
  onCloseDiff?: () => void
}

export function TaskChatInputV2({
  taskAgentId,
  activeSession,
  onAddPendingSession,
  onClearPendingSession,
  payload,
  scrollToBottom,
  repoNwo,
  task,
  branchAliveChannel,
  onViewDiff,
  onCloseDiff,
}: TaskChatInputProps) {
  const {pull, steeringEnabled, currentUserCanPush, agentQuotaSnapshot} = payload
  const isBlockedByQuota =
    agentQuotaSnapshot?.can_start_session === false && copilotFeatureFlags.codingAgentTBBQuotaBanner
  const viewMode = useViewMode()
  const tooltipDirection = viewMode === 'sidePanel' ? 'n' : undefined
  const [failedToSubmit, setFailedToSubmit] = useState(false)
  const {data: agentInfo, isLoading: isAgentInfoLoading} = useAgentInfo(taskAgentId)
  const {data: topLevelAgents, isLoading: isLoadingTopLevelAgents} = useTopLevelAgents(repoNwo)
  const {setPendingChat} = useChatStreamContext()
  const agentNotEnabled =
    taskAgentId &&
    !isLoadingTopLevelAgents &&
    topLevelAgents &&
    topLevelAgents.length > 0 &&
    !topLevelAgents.some(agent => Number(agent.id) === taskAgentId)
  const name = agentInfo?.display_name?.toLowerCase() || 'agent'
  const is3PCodingAgent = !isAgentInfoLoading && ['claude', 'codex'].includes(name)
  const {isUsingIntentDetection} = useIntentDetection(activeSession)
  const {mode, setMode} = useIntentDetectionContext()
  const steerable = !!activeSession && !is3PCodingAgent && !isUsingIntentDetection
  const startingSession = activeSession && isPendingSession(activeSession)
  const activeSessionOwnerLogin = activeSession?.user_login ?? null
  const {currentUserIsSessionOwner, currentUser} = useCurrentUser()
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const inputAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const allChronSuggestions = useChronicleAutocomplete(text)
  const [chronicleDismissed, setChronicleDismissed] = useState(false)
  const chronicleSuggestions =
    (!taskAgentId || isCopilotCodingAgent(agentInfo?.slug)) && !chronicleDismissed ? allChronSuggestions : []
  const containerRef = useRef<HTMLDivElement | null>(null)
  const canAutoUnarchive = !!task.archived_at && pull?.state !== 'merged'
  const {mutateAsync: unarchiveTask} = useUnarchiveTask({taskId: task.id, repoNwo})

  // Model selection
  const [selectedModelId, setSelectedModelId] = useLatestAgentModel()
  const {plan} = useEntitlement()
  const shouldShowModelPicker =
    copilotFeatureFlags.codingAgentModelSelection &&
    (copilotFeatureFlags.codingAgentModelSelectionAllSkus ||
      plan === CopilotPlan.IndividualPro ||
      plan === CopilotPlan.IndividualProPlus ||
      plan === CopilotPlan.IndividualMax ||
      plan === CopilotPlan.IndividualStudent) &&
    isCopilotOrCustomAgent({topLevelAgentId: taskAgentId?.toString()}, agentInfo?.slug)

  const stopButton = activeSession ? (
    <TaskStopButton
      key={activeSession.id}
      session={activeSession}
      nwo={repoNwo}
      currentUserCanPush={currentUserCanPush}
      tooltipDirection={tooltipDirection}
    />
  ) : null

  const handleCommandCreated = useCallback(
    (commandResponse: CommandCreatedResponse, problemStatement: string) => {
      const sessionId = commandResponse.session_id || activeSession?.id
      if (!sessionId) return

      const queryKey = ['agent-session-commands', sessionId]
      const command: Command = {
        id: commandResponse.id,
        external_id: commandResponse.external_id ?? null,
        user_id: -1,
        session_id: sessionId,
        content: problemStatement,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        state: (commandResponse.state as Command['state']) || 'queued',
        error: null,
      }

      const currentData = queryClient.getQueryData<CommandsResponse>(queryKey)
      queryClient.setQueryData(queryKey, {
        commands: [...(currentData?.commands ?? []), command],
      })

      onClearPendingSession()
    },
    [activeSession, queryClient, onClearPendingSession],
  )

  // Image upload handling
  const repositoryId = task.repository?.id?.toString()
  const {
    onPaste,
    dropTargetProps,
    isDraggedOver,
    isUploading: isImageUploading,
  } = useMarkdownImageHandling({
    textAreaRef: inputAreaRef,
    repositoryId,
  })

  const branchArtifact = getBranchArtifactData(task?.artifacts)
  const baseRef = (!baseRefMatchesHeadRef(branchArtifact) && branchArtifact?.base_ref) || pull?.base_ref_name
  const headRef = branchArtifact?.head_ref || pull?.head_ref_name
  const hasTaskChatInputHeader = !!baseRef && !!headRef && viewMode !== 'sidePanel'

  // Reset error state when user starts typing, with a small delay to prevent swallowing the first character
  useEffect(() => {
    const inputElement = inputAreaRef.current
    if (!inputElement) return

    let timeout: NodeJS.Timeout
    const handleInput = () => {
      if (failedToSubmit) {
        // Use a small timeout to ensure the character is processed first
        timeout = setTimeout(() => {
          setFailedToSubmit(false)
        }, 0)
      }
    }

    inputElement.addEventListener('input', handleInput)
    return () => {
      inputElement.removeEventListener('input', handleInput)
      if (timeout) clearTimeout(timeout)
    }
  }, [failedToSubmit, inputAreaRef])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!inputAreaRef?.current) return

    setText((e.target as HTMLTextAreaElement).value)
    setChronicleDismissed(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const {createSession} = useCreateAPISession({
    onError: () => {
      setFailedToSubmit(true)
      onClearPendingSession()
    },
    onCommandCreated: isUsingIntentDetection ? handleCommandCreated : undefined,
  })

  // Permission prompt handling for CCA sessions
  const {
    pendingPrompt,
    isExpired: isPendingPromptExpired,
    isPending: isCommandPending,
    handlePromptRespond,
  } = usePromptResponseSubmission({
    sessionId: activeSession?.id,
    enabled: !!activeSession && isActiveSession(activeSession),
    sessionState: activeSession?.state,
    onError: () => setFailedToSubmit(true),
  })

  const {createCommand} = useCreateCommand({
    onError: () => setFailedToSubmit(true),
  })

  const getBlockedText = (): string | React.ReactNode | undefined => {
    if (agentNotEnabled) {
      return 'Your organization has removed access for this agent. Contact your administrator for more information.'
    }

    // 3P agent is running, can't steer
    if (activeSession && is3PCodingAgent) {
      return `Wait until the session is complete before steering ${agentInfo?.display_name || 'agent'} sessions`
    }

    // User doesn't have access to steering features
    if (!steeringEnabled) {
      return 'Steering Copilot requires an active subscription'
    }

    if (task.archived_at && !canAutoUnarchive) {
      return 'This session has been archived'
    }

    // User doesn't have write access
    if (!currentUserCanPush) {
      return 'You do not have write permission on the repository'
    }

    // Pull is closed/merged, no steering allowed
    if (pull?.state === 'merged') {
      return (
        <>
          This pull request was merged. To continue iterating,{' '}
          <Link href={`/${repoNwo}/agents`} inline>
            start a new session
          </Link>
          .
        </>
      )
    } else if (pull?.state === 'closed') {
      return 'This pull request was closed. To continue iterating, reopen the pull request.'
    }

    // Single session view — also check payload data directly to avoid a flash
    // during render transitions where the context value may lag behind the actual data.
    const isPayloadOwner = payload.sessions?.some(s => s.user_id === currentUser?.id)
    if (!currentUserIsSessionOwner && currentUser && !isPayloadOwner) {
      if (steerable) {
        return `Only ${activeSessionOwnerLogin ?? 'the session owner'} can steer this session`
      }

      return (
        <>
          Only the author of the task can ask for changes.{' '}
          {pull && (
            <>
              <Link href={pull.url} inline>
                View pull request
              </Link>{' '}
              to request more changes.
            </>
          )}
        </>
      )
    }

    if (isBlockedByQuota) {
      const message = "You don't have sufficient AI credits to start or steer a session."

      if (
        agentQuotaSnapshot?.copilot_plan === CopilotPlan.Business ||
        agentQuotaSnapshot?.copilot_plan === CopilotPlan.Enterprise
      ) {
        return `${message} Contact your administrator for more information.`
      }

      return (
        <>
          {message}{' '}
          <Link href={MANAGE_BUDGET_URL} inline>
            Manage your budget
          </Link>
          {agentQuotaSnapshot?.can_upgrade_plan ? (
            <>
              {' or '}
              <Link href={UPGRADE_PLAN_URL} inline>
                upgrade your plan
              </Link>
            </>
          ) : null}
          {'.'}
        </>
      )
    }

    return undefined
  }

  // Consider the input blocked if there's any blocked text to show
  const blockedText = getBlockedText()
  const isBlocked = !!blockedText

  // Not showing the quota banner when input is blocked
  const shouldShowQuotaBanner = !isBlocked

  // Get the container height dynamically for scroll offset (includes header + input)
  const getContainerOffset = () => containerRef.current?.offsetHeight ?? 0

  const handleActiveSessionCommandSubmit = (message: string) => {
    if (!steerable || !activeSession) return

    createCommand({
      sessionId: activeSession.id,
      content: message,
    })

    setText('')
  }

  const handleSubmit = async () => {
    // Always reset the error state when attempting a new submission
    setFailedToSubmit(false)

    try {
      // Prevent submitting empty messages or when blocked/starting/uploading
      if (!text.trim() || isBlocked || startingSession || isImageUploading) {
        return
      }

      if (steerable) {
        handleActiveSessionCommandSubmit(text)
        scrollToBottom()
        return
      }

      // For intent detection POC, chat-based sessions just route
      // through create session for task so if it needs to spawn a new
      // agent session (terminating the chat) it can.
      if (!isUsingIntentDetection) {
        // Show the pending session immediately
        onAddPendingSession(text, {agentId: taskAgentId, isPRComment: false})
      } else {
        setPendingChat(text)
      }

      // Unarchive must complete before creating a session
      if (canAutoUnarchive) {
        await unarchiveTask()
      }

      // Clear the input
      setText('')
      onCloseDiff?.()

      createSession({
        nwo: repoNwo,
        taskId: task.id,
        problemStatement: text,
        ...(shouldShowModelPicker && selectedModelId ? {model: selectedModelId} : {}),
        agentId: taskAgentId,
        eventType: 'agent_task_post_session_steering',
        intentDetectionMode: mode,
      })

      requestAnimationFrame(() => {
        scrollToBottom({offset: getContainerOffset()})
      })
    } catch {
      setFailedToSubmit(true)

      // Throwing this error will repopulate the text in the input
      throw new ChatInputValidationError(text)
    }
  }

  const placeHolderText = startingSession
    ? 'Waiting for agent to start session...'
    : steerable
      ? 'Steer active session while agent is working'
      : 'What would you like to do next?'

  return (
    <div
      className={clsx(styles.container, isDraggedOver && styles.isDraggedOver)}
      ref={containerRef}
      {...dropTargetProps}
    >
      {shouldShowQuotaBanner && (
        <div className={clsx(hasTaskChatInputHeader && styles.quotaBannerWithHeader)}>
          <QuotaBanner agentQuotaSnapshot={agentQuotaSnapshot} />
        </div>
      )}
      {hasTaskChatInputHeader && (
        <TaskChatInputHeader
          pull={pull}
          repoNwo={repoNwo}
          taskId={task.id}
          baseRef={baseRef}
          headRef={headRef}
          branchAliveChannel={branchAliveChannel}
          // `waiting_for_user` is technically `isActiveSession()`, but for the
          // chat-input header (Diff button, Create-pull-request button) it
          // should be treated as paused — the runtime container has exited
          // and there's no in-flight work that would invalidate the diff or
          // racing PR creation. Without this, after a resume completes and
          // the old frozen `waiting_for_user` session becomes `activeSession`
          // again, the Create-pull-request button stays disabled.
          isSessionInProgress={
            activeSession ? isActiveSession(activeSession) && activeSession.state !== 'waiting_for_user' : false
          }
          onViewDiff={onViewDiff}
        />
      )}
      {pendingPrompt && !isBlocked ? (
        isPendingPromptExpired ? (
          <div className={clsx(styles.inputWrapper, styles.inputWrapperWithToolbar, styles.withImageUpload)}>
            <BlockedTaskInput>This pending prompt has expired. Start a new task to continue.</BlockedTaskInput>
          </div>
        ) : (
          <PromptInput
            key={pendingPrompt.toolCallId}
            prompt={pendingPrompt}
            onRespond={handlePromptRespond}
            disabled={isCommandPending}
          />
        )
      ) : (
        <form
          onSubmit={e => {
            e.preventDefault()
            handleSubmit()
          }}
          id="task-chat-input-form"
          data-testid="task-chat-input-form"
        >
          <FormControl>
            <FormControl.Label visuallyHidden>Send message</FormControl.Label>
            {isBlocked ? (
              <div className={clsx(styles.inputWrapper, styles.inputWrapperWithToolbar, styles.withImageUpload)}>
                <BlockedTaskInput>{blockedText}</BlockedTaskInput>
              </div>
            ) : isUsingIntentDetection ? (
              <div className={clsx(styles.inputWrapper, styles.inputWrapperWithToolbar, styles.withImageUpload)}>
                <Stack direction="vertical" className={styles.inputContainer}>
                  <textarea
                    className={clsx(styles.input, styles.withImageUpload, (isFocused || text) && styles.inputFocused)}
                    onSubmit={handleSubmit}
                    aria-label={placeHolderText}
                    value={text}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder={placeHolderText}
                    disabled={isBlocked}
                    ref={inputAreaRef}
                    rows={isFocused || text ? 3 : 1}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                  <ChronicleAutocomplete
                    suggestions={chronicleSuggestions}
                    onSelect={setText}
                    onDismiss={() => setChronicleDismissed(true)}
                    textAreaRef={inputAreaRef}
                    currentText={text}
                  />
                  <Stack direction="horizontal" className={styles.modeAndButtonContainer}>
                    <span className={styles.modeSelector}>
                      <ModeSelector selectedMode={mode} setMode={setMode} />
                    </span>
                    {shouldShowModelPicker && (
                      <ModelPicker
                        selectedModelId={selectedModelId}
                        onUpdateModel={setSelectedModelId}
                        isTokenBasedBillingEnabled={payload.useTokenBasedBilling}
                      />
                    )}
                    <div className={styles.sendButtonGroup}>
                      {stopButton}
                      <IconButton
                        variant="invisible"
                        size="medium"
                        icon={PaperAirplaneIcon}
                        aria-label="Send now"
                        tooltipDirection={tooltipDirection}
                        inactive={startingSession || isBlocked}
                        aria-disabled={startingSession || isBlocked}
                        onClick={handleSubmit}
                        className={styles.sendButton}
                      />
                    </div>
                  </Stack>
                </Stack>
              </div>
            ) : (
              <ChatInputDisplay
                ref={inputAreaRef}
                value={text}
                onChange={setText}
                onSubmit={handleSubmit}
                placeholder={placeHolderText}
                disabled={isBlocked}
                isSubmitting={startingSession || isImageUploading}
                sendButtonAriaLabel="Send now"
                allowEmptySubmit
                onPaste={onPaste}
                stopSlot={stopButton}
                tooltipDirection={tooltipDirection}
                afterTextarea={
                  <ChronicleAutocomplete
                    suggestions={chronicleSuggestions}
                    onSelect={setText}
                    onDismiss={() => setChronicleDismissed(true)}
                    textAreaRef={inputAreaRef}
                    currentText={text}
                  />
                }
                toolbarSlot={
                  <ImageAttachButton
                    textAreaRef={inputAreaRef}
                    repositoryId={repositoryId}
                    tooltipDirection={tooltipDirection}
                  />
                }
                toolbarEndSlot={
                  shouldShowModelPicker && !steerable ? (
                    <ModelPicker
                      selectedModelId={selectedModelId}
                      onUpdateModel={setSelectedModelId}
                      isTokenBasedBillingEnabled={payload.useTokenBasedBilling}
                    />
                  ) : undefined
                }
              />
            )}
            {failedToSubmit && !isBlocked && (
              <FormControl.Validation variant="error" className="pt-2">
                Failed to submit latest message
              </FormControl.Validation>
            )}
          </FormControl>
        </form>
      )}
    </div>
  )
}
